// src/hooks/ui/useConfirmGate.js
//
// The confirmation GATE — the thing that stands between "the user pressed the
// destructive button" and "the action ran". One state machine, shared by
// ConfirmDialog, ConfirmDialogInline and BulkActionProgressModal, so the three
// shells cannot drift the way their three hand-rolled typed fields did.
//
// ── Why `activate()` is the only way through ─────────────────────────────────
// `useModalKeyboard` binds a DOCUMENT-level Enter. Historically that ran the
// confirm directly, so the keyboard path and the click path each enforced the
// gate on their own — and `ConfirmDialog.enterGuard.test.js` exists because
// they once disagreed (hiding the confirm button removed the CLICK path but
// not the keyboard one). A multi-step gate makes that failure far worse: with
// no typed phrase to satisfy, a single Enter would skip both taps entirely,
// and it is reachable on touch — an iPad or iPhone with a Bluetooth keyboard
// is still `pointer: coarse`.
//
// So there is exactly ONE function that can reach `onCommit`, and click, Enter
// and anything added later all call it. The gate cannot be bypassed by a path
// that forgot to check, because there is no other path.
//
// ── Modes ────────────────────────────────────────────────────────────────────
//   'none'    single confirm — reversible, low-consequence actions
//   'phrase'  type a word or a name (today's behaviour; the default)
//   'morph'   two-step: tap arms, tap again commits
//
// DOM-free on purpose: the measuring and the animation live in
// ConfirmMorphActions, this file stays a pure state machine so the security
// behaviour is unit-testable without a renderer. Guard: useConfirmGate.test.js

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export const CONFIRM_GATE = {
  NONE: 'none',
  PHRASE: 'phrase',
  MORPH: 'morph',
};

/**
 * How long the armed state refuses input after arming.
 *
 * The accident this defends against is a reflex double-tap — tapping through
 * without reading. Typical inter-tap intervals sit around 100-250ms, so the
 * window covers the morph (320ms) plus a beat. It is NOT a countdown: nothing
 * disarms on a timer, because a control that changes under a descending thumb
 * manufactures the very mis-tap it is trying to prevent.
 */
export const CONFIRM_GATE_INERT_MS = 400;

export function matchesConfirmPhrase(value, phrase, caseInsensitive) {
  if (typeof phrase !== 'string' || phrase.length === 0) return false;
  const typed = String(value ?? '').trim();
  return caseInsensitive
    ? typed.toLowerCase() === phrase.trim().toLowerCase()
    : typed === phrase;
}

export function useConfirmGate({
  mode = CONFIRM_GATE.NONE,
  phrase,
  phraseCaseInsensitive = false,
  /** Extra precondition, e.g. an unticked attestation box. Blocks arming too. */
  enabled = true,
  reduced = false,
  onCommit,
} = {}) {
  const [phraseValue, setPhraseValue] = useState('');
  const [armed, setArmed] = useState(false);
  const [arming, setArming] = useState(false);
  const armTimerRef = useRef(null);

  const isPhrase = mode === CONFIRM_GATE.PHRASE
    && typeof phrase === 'string' && phrase.length > 0;
  const isMorph = mode === CONFIRM_GATE.MORPH;

  const phraseMatches = isPhrase
    ? matchesConfirmPhrase(phraseValue, phrase, phraseCaseInsensitive)
    : false;

  /** The gate's own condition — does NOT include busy / label / free-text. */
  const satisfied = isPhrase ? phraseMatches : (isMorph ? armed : true);

  const clearArmTimer = useCallback(() => {
    if (armTimerRef.current) {
      clearTimeout(armTimerRef.current);
      armTimerRef.current = null;
    }
  }, []);

  useEffect(() => clearArmTimer, [clearArmTimer]);

  const disarm = useCallback(() => {
    clearArmTimer();
    setArmed(false);
    setArming(false);
  }, [clearArmTimer]);

  const arm = useCallback(() => {
    clearArmTimer();
    setArmed(true);
    setArming(true);
    armTimerRef.current = setTimeout(() => {
      setArming(false);
      armTimerRef.current = null;
    }, reduced ? 0 : CONFIRM_GATE_INERT_MS);
  }, [clearArmTimer, reduced]);

  // Disarm when the app comes back to the foreground. This is an installed PWA
  // on a phone that gets pocketed: returning minutes later to an armed red
  // capsule reading "Yes, delete Carla Núñez", with no memory of arming it, is
  // one reflex tap away from a real deletion. Deliberately NOT a countdown —
  // see CONFIRM_GATE_INERT_MS.
  useEffect(() => {
    if (!isMorph || !armed) return undefined;
    const onVisibility = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'visible') disarm();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [isMorph, armed, disarm]);

  // A precondition that goes away must take the armed state with it — e.g.
  // un-ticking the attestation box on a break-glass confirm.
  useEffect(() => {
    if (!enabled && armed) disarm();
  }, [enabled, armed, disarm]);

  // Changing the gate itself resets it. CustomerBulkDeleteModal swaps its
  // phrase mid-flight while a readiness check resolves, and a stale armed or
  // stale typed value must never survive that.
  useEffect(() => {
    setPhraseValue('');
    disarm();
  }, [mode, phrase, disarm]);

  /**
   * The ONLY route to onCommit. Click, Enter, and anything added later must
   * come through here — see the header note.
   */
  const activate = useCallback(() => {
    if (!enabled) return;
    if (isMorph) {
      if (arming) return;                 // inert window: reflex tap lands on nothing
      if (!armed) { arm(); return; }      // first press arms, commits nothing
    }
    if (isPhrase && !phraseMatches) return;
    onCommit?.();
  }, [enabled, isMorph, arming, armed, arm, isPhrase, phraseMatches, onCommit]);

  return useMemo(() => ({
    mode: isPhrase ? CONFIRM_GATE.PHRASE : (isMorph ? CONFIRM_GATE.MORPH : CONFIRM_GATE.NONE),
    isPhrase,
    isMorph,
    phraseValue,
    setPhraseValue,
    phraseMatches,
    satisfied,
    armed,
    arming,
    arm,
    disarm,
    activate,
  }), [isPhrase, isMorph, phraseValue, phraseMatches, satisfied, armed, arming, arm, disarm, activate]);
}
